import { createSlice } from '@reduxjs/toolkit';
import isEmpty from '../utils/is-empty';
import setAuthToken from '../utils/setAuthToken';
import * as REQUESTS from '../api/authRequests';
import { setAlert } from './alertSlice';
import { LOGIN } from '../constants/routes';

const initialState = {
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  loading: false,
  user: null,
  followers: null,
  filteredFollowers: null,
  following: null,
  filteredFollowing: null,
  error: null,
  message: null,
  notificationLoading: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    authStart(state) {
      state.loading = true;
      state.error = null;
    },
    setCurrentUser(state, action) {
      state.isAuthenticated = !isEmpty(action.payload);
      state.user = action.payload.data;
      state.followers = action.payload.data.followers;
      state.filteredFollowers = action.payload.data.followers;
      state.following = action.payload.data.following;
      state.filteredFollowing = action.payload.data.following;
      state.loading = false;
    },

    setToken(state, action) {
      state.isAuthenticated = !isEmpty(action.payload);
      state.token = action.payload;
      state.loading = false;
    },
    setFilteredFollowers(state, action) {
      state.filteredFollowers = state.followers.filter((follower) => {
        // build up a regular expression from text as well as set second param as gi, which means globally insesitive
        const regex = new RegExp(`${action.payload}`, 'gi');
        return (
          follower.user.name.match(regex) || follower.user.username.match(regex)
        );
      });
      state.loading = false;
    },
    setFilteredFollowing(state, action) {
      state.filteredFollowing = state.following.filter((user) => {
        // build up a regular expression from text as well as set second param as gi, which means globally insesitive
        const regex = new RegExp(`${action.payload}`, 'gi');
        return user.user.name.match(regex) || user.user.username.match(regex);
      });
      state.loading = false;
    },
    authFailure(state, action) {
      state.token = null;
      state.isAuthenticated = false;
      state.user = null;
      state.loading = false;
      state.error = action.payload ? action.payload : 'Could not connect';
    },
    setLogout(state) {
      state.token = null;
      state.isAuthenticated = false;
      state.user = null;
      state.loading = false;
    },
    authComplete(state) {
      state.loading = false;
      state.error = null;
    },
    setMessage(state, action) {
      state.message = action.payload;
      state.loading = false;
    },
    setNotificationLoading(state) {
      state.notificationLoading = !state.notificationLoading;
    },
    setLoading(state) {
      state.loading = !state.loading;
    },
  },
});

export const {
  authStart,
  setCurrentUser,
  authFailure,
  authComplete,
  setLoading,
  setToken,
  setLogout,
  setMessage,
  setFilteredFollowers,
  setFilteredFollowing,
  setCreatedStream,
  setEndedStream,
  setNotificationLoading,
} = authSlice.actions;

export default authSlice.reducer;

// thunks

//load user
export const loadUser = () => async (dispatch) => {
  try {
    const data = await REQUESTS.getMe();
    console.log(data);
    dispatch(setCurrentUser(data));
  } catch (err) {
    dispatch(authFailure(err.response.data.error));
    dispatch(setAlert(err.response.data.error, 'danger'));
  }
};

//register user
export const registerUser = (formData, history) => async (dispatch) => {
  try {
    console.log('in slice');
    dispatch(authStart());
    const data = await REQUESTS.register(formData);
    const { success } = data;
    if (success) {
      // Setting token to authorisation header in axios
      // await setAuthToken(token);

      // dispatch(loadUser());
      // dispatch(setToken(token));

      dispatch(setAlert('Registered Successfully!', 'success'));
      dispatch(authComplete());
      history.push(LOGIN);
    }
  } catch (err) {
    dispatch(setLoading());
    dispatch(setAlert(err.response.data.error, 'danger'));
    dispatch(authFailure(err.response.data.error));
  }
};

//login
export const loginUser = (formData) => async (dispatch) => {
  try {
    dispatch(authStart());
    const data = await REQUESTS.login(formData);
    const { success, token } = data;
    if (success) {
      // Setting token to authorisation header in axios
      await setAuthToken(token);

      dispatch(loadUser());
      dispatch(setToken(token));

      dispatch(setAlert('Logged in Successfully!', 'success'));
      dispatch(authComplete());
    }
  } catch (err) {
    dispatch(setLoading());
    dispatch(setAlert(err.response.data.error, 'danger'));
    console.log(err);
    dispatch(authFailure(err.response.data.error));
  }
};

// update user details
export const updateDetails = (formData) => async (dispatch) => {
  try {
    dispatch(setLoading());

    const data = await REQUESTS.updateDetails(formData);
    const { success } = data;

    if (success) {
      dispatch(loadUser());
      dispatch(setAlert('Updated details successfully!', 'success'));
    }
  } catch (err) {
    dispatch(setLoading());
    dispatch(setAlert(err.response.data.error, 'danger'));
    // dispatch(authFailure(err.response.data.error));
  }
};

// update user password
export const updatePassword = (formData) => async (dispatch) => {
  try {
    dispatch(setLoading());
    const data = await REQUESTS.updatePassword(formData);
    const { success } = data;

    if (success) {
      dispatch(loadUser());
      dispatch(setAlert('Updated password successfully!', 'success'));
    }
  } catch (err) {
    dispatch(setLoading());
    dispatch(setAlert(err.response.data.error, 'danger'));
    // dispatch(authFailure(err.response.data.error));
  }
};

//logout
export const logout = () => async (dispatch) => {
  await REQUESTS.logout();
  dispatch(setLogout());

  dispatch(setAlert('Logged out successfully', 'success'));
};

// send email for reset password
export const sendEmail = (email) => async (dispatch) => {
  try {
    dispatch(setLoading());
    const data = await REQUESTS.forgotPassword(email);
    const { success } = data;

    if (success) {
      dispatch(setLoading());
      dispatch(setAlert('Email Sent!', 'success'));
    }
  } catch (err) {
    dispatch(setLoading());
    dispatch(setAlert(err.response.data.error, 'danger'));
  }
};

// reset password
export const resetPassword = (token, password, history) => async (dispatch) => {
  try {
    dispatch(setLoading());
    const data = await REQUESTS.resetPassword(token, password);
    const { success } = data;

    if (success) {
      dispatch(setLoading());
      dispatch(setAlert('Password has been changed successfully!', 'success'));
      history.push(LOGIN);
    }
  } catch (err) {
    dispatch(setLoading());
    dispatch(setAlert(err.response.data.error, 'danger'));
  }
};

// addCoins
export const addCoins = (formData) => async (dispatch) => {
  try {
    dispatch(setLoading());
    const resData = await REQUESTS.addCoins(formData);
    const { success, data } = resData;

    if (success) {
      dispatch(setMessage(data));
      dispatch(loadUser());
      dispatch(setAlert(data, 'success'));
    }
  } catch (err) {
    dispatch(setLoading());
    dispatch(setAlert(err.response.data.error, 'danger'));
  }
};

// share Coins
export const shareCoins = (formData) => async (dispatch) => {
  try {
    dispatch(setLoading());
    const resData = await REQUESTS.shareCoins(formData);
    const { success, data } = resData;

    if (success) {
      dispatch(setMessage(data));
      dispatch(loadUser());
      dispatch(setAlert(data, 'success'));
    }
  } catch (err) {
    dispatch(setLoading());
    dispatch(setAlert(err.response.data.error, 'danger'));
  }
};

// share Coins
export const clearNotifications = () => async (dispatch) => {
  try {
    dispatch(setNotificationLoading());
    const resData = await REQUESTS.clearNotifications();
    const { success } = resData;

    if (success) {
      dispatch(loadUser());
      dispatch(setNotificationLoading());
      dispatch(setAlert('Notifications Cleared!', 'success'));
    }
  } catch (err) {
    dispatch(setNotificationLoading());
    dispatch(setAlert(err.response.data.error, 'danger'));
  }
};
